// frontend/src/components/orcamento/ComoFunciona.js
// Modal "Como funciona" do Orçamento com Monte Carlo: explica ao operador, em
// linguagem de uso, de onde vêm os números, o que os selos querem dizer e o
// passo a passo para fechar o orçamento. Texto fixo; não chama o backend.
import React, { useEffect } from 'react';

const SELOS = [
  ['verde', 'Confiável', 'Pelo menos 24 meses de histórico e gasto estável (varia menos de 35 %). Pode aceitar o valor sugerido.'],
  ['amarelo', 'Conferir', 'Histórico médio ou gasto que oscila entre 35 % e 100 %. Olhe a análise da categoria antes de aceitar.'],
  ['vermelho', 'Pouco previsível', 'Pouco histórico, gasto muito irregular ou categoria que parou de ter lançamento. Digite o valor que faz sentido.'],
  ['decisao', 'Decisão', 'Política da empresa, não previsão (ex.: distribuição de lucros, pró-labore, empréstimos). O sistema não projeta; o valor é seu.'],
];

export default function ComoFunciona({ aberto, onFechar }) {
  useEffect(() => {
    if (!aberto) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aberto, onFechar]);

  if (!aberto) return null;
  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onFechar}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content op-ajuda">
          <div className="modal-header bg-white">
            <div>
              <h5 className="modal-title" style={{ color: '#212529' }}>
                <i className="fas fa-circle-question me-2" style={{ color: '#c9a227' }}></i>Como funciona a projeção do orçamento
              </h5>
              <div className="small" style={{ color: '#6c757d' }}>O que o sistema faz por você e o que continua sendo decisão sua</div>
            </div>
            <button type="button" className="btn-close" onClick={onFechar} aria-label="Fechar"></button>
          </div>
          <div className="modal-body">
            <h6>1. De onde vêm os números</h6>
            <p>
              O sistema olha os <b>títulos pagos</b> de cada categoria nos últimos 36 meses (o mês corrente fica de fora
              por estar incompleto) e identifica três coisas: o <b>patamar atual</b> do gasto, se ele vem <b>crescendo ou
              caindo</b> e se há <b>meses que sempre pesam mais</b> (13º em dezembro, por exemplo).
            </p>
            <p>
              Com isso ele simula o ano <b>10.000 vezes</b> (método de Monte Carlo), cada vez com variações parecidas com as
              que já aconteceram. O valor que aparece na célula é o <b>P50</b>: o resultado do meio dessas simulações.
              Metade dos cenários ficou abaixo dele e metade acima.
            </p>
            <p className="mb-1">Ao passar o mouse numa célula você vê a <b>faixa provável</b>:</p>
            <ul>
              <li><b>P10</b> — cenário otimista: só 10 % das simulações gastaram menos que isso.</li>
              <li><b>P90</b> — cenário conservador: só 10 % gastaram mais que isso.</li>
            </ul>

            <h6>2. O que significam as cores</h6>
            <div className="op-ajuda-selos">
              {SELOS.map(([cls, nome, texto]) => (
                <div key={cls}><span className={`op-selo ${cls}`}></span><b>{nome}</b> — {texto}</div>
              ))}
            </div>

            <h6>3. Passo a passo</h6>
            <ol>
              <li>Escolha o ano a projetar. Só é possível projetar um ano ainda não coberto pelo histórico.</li>
              <li>Comece pelas <b>vermelhas</b> e pelas de <b>decisão</b>: elas não têm valor confiável e precisam de você.</li>
              <li>Clique no <b>nome da categoria</b> para abrir a análise: gráfico do histórico, médias de 3, 6 e 12 meses,
                lista de títulos e a <b>calculadora</b> (ex.: média de 12 meses + 5 %). "Usar estes valores" preenche os 12 meses.</li>
              <li>Edite qualquer célula digitando o valor. A célula editada fica <b>amarela</b>; a linha
                <b> Resultado</b> e o rodapé por pilar (receita, despesa e resultado) recalculam na hora.</li>
              <li>Clique em <b>Gravar orçamento</b>. Despesas e receitas são gravadas cada uma no seu orçamento; o que
                você digitou vai como manual e o resto como projeção.</li>
            </ol>

            <h6>4. Receitas e resultado</h6>
            <p>
              As <b>receitas</b> aparecem abaixo das despesas e são projetadas do mesmo jeito, a partir dos títulos
              recebidos, com os mesmos selos. A última linha da grade é o <b>resultado</b> de cada mês: receitas menos
              despesas, já com as suas edições. Prejuízo aparece com sinal de menos.
            </p>
            <p>
              Na simulação, receita e despesa são sorteadas <b>no mesmo cenário</b>: o resultado de cada uma das 10.000
              simulações é a receita daquele cenário menos a despesa daquele cenário. Por isso os percentis do resultado
              não são a simples diferença dos percentis de cada lado.
            </p>

            <h6>5. Aba Simulação</h6>
            <p>
              Mostra o <b>resultado do ano</b> nas 10.000 simulações, os três cenários (pessimista, provável e otimista),
              a <b>chance de prejuízo</b> (em quantos cenários o ano fecha abaixo de zero) e quais categorias, de receita
              ou de despesa, concentram a incerteza. Digite uma <b>meta de resultado</b> para ver a chance de batê-la.
              A conta já considera as edições que você fez na grade.
            </p>
            <p>
              O quadro <b>Cenário por pilar</b> faz a mesma leitura para cada departamento: a barra vai do P10 ao P90 do
              resultado do pilar, o ponto é o P50 e a linha escura marca o zero. Ao lado ficam a margem e a chance de
              prejuízo de cada pilar.
            </p>
            <p>
              O quadro <b>Validação</b> mostra quanto o método teria errado se tivesse sido usado no ano passado,
              comparado a simplesmente repetir o ano anterior. Se o erro do método for maior, prefira as médias da
              análise por categoria.
            </p>

            <h6>6. Ajustes que mudam a projeção</h6>
            <ul>
              <li><b>Marcar como decisão</b>: tira a categoria da simulação e deixa o valor por sua conta. Vale só para o
                lado (receita ou despesa) da categoria.</li>
              <li><b>Ignorar no cálculo</b> (em um outlier da análise): desconsidera um mês fora do padrão, como uma
                compra pontual, e refaz a projeção. Dá para reconsiderar depois.</li>
              <li>O botão <i className="fas fa-rotate"></i> recalcula a projeção com os títulos mais recentes.</li>
            </ul>

            <h6>7. Aba Premissas</h6>
            <p>
              As premissas são o que você sabe (ou supõe) sobre o ano que o histórico não mostra. Elas valem para
              <b> todos os 10.000 cenários</b>, e o topo da aba mostra na hora como o resultado mudou. Tudo é gravado
              sozinho, por cliente e ano; <b>Voltar ao padrão</b> zera as premissas.
            </p>
            <ul>
              <li><b>Pessoal</b>: dissídio entre um mínimo e um máximo (cada cenário sorteia um valor) a partir do mês da
                data-base, e quanto do pessoal cresce junto com a receita. "Ajustar" escolhe quais categorias contam
                como pessoal.</li>
              <li><b>Receita e estrutura</b>: crescimento de contratos por mês, reajuste de preço ao longo do ano,
                quanto do faturamento não entra no caixa e a inflação das despesas administrativas.</li>
              <li><b>Tributário e sócios</b>: um custo extra sobre a receita (em pontos percentuais), um valor fixo por
                mês para distribuição ou pró-labore e o total de investimentos do ano.</li>
              <li><b>Eventos incertos</b>: algo que pode ou não acontecer (um contrato novo, uma multa). Você diz o
                valor por mês, a partir de quando, a chance e o pilar; em cada cenário o sistema sorteia se acontece.</li>
            </ul>
            <p>
              Na grade, o que vem das premissas aparece com o selo <span className="op-selo-p">P</span> e não é
              editável; categorias com valor fixo também ficam travadas (mude o valor na aba Premissas).
            </p>

            <h6>8. Visão consolidada (Advantec + ADVWG)</h6>
            <p>
              O botão <b>Consolidar empresas</b> soma as projeções das duas empresas do grupo <b>cenário a cenário</b>:
              cada uma usa as suas próprias premissas e ajustes, e o resultado de cada um dos 10.000 cenários é a soma
              das duas. Categorias e pilares com o mesmo nome viram uma linha só; os selos <span className="op-emp">ADV</span>
              mostram de qual empresa vem cada valor.
            </p>
            <p>
              Não há eliminação de operações entre as empresas (o que uma paga à outra aparece nos dois lados), e a
              visão é <b>só de leitura</b>: para editar valores, premissas ou ajustes, abra a empresa.
            </p>

            <div className="op-ajuda-nota">
              <i className="fas fa-lightbulb me-2"></i>
              A projeção é um ponto de partida, não uma resposta: ela supõe que o futuro se parece com o passado.
              Contratações, novos contratos ou cortes já decididos precisam entrar pelas suas mãos.
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onFechar}>Entendi</button>
          </div>
        </div>
      </div>
    </div>
  );
}
